import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { threadTimeline } from '@/redux/selectors/pieveSelectors/assistantSelectors.js';
import { threadPendingInteractions } from '@/redux/selectors/pieveSelectors/threadActivitySelectors.js';
import ApprovalCard from './ApprovalCard.jsx';
import ConnectionCard from './ConnectionCard.jsx';
import NoticeLine from './NoticeLine.jsx';
import MarkdownText from '@/components/patterns/MarkdownText.jsx';
import RequestBlock from './RequestBlock.jsx';
import StepTrail from './StepTrail.jsx';

/** The conversation in order: requests, step trails, replies, approvals, connection cards and notices. Follows new content to the bottom. */
const ThreadTimeline = ({ threadId }) => {
  const blocks = useSelector((state) => threadTimeline(state, threadId));
  const pendingInteractions = useSelector((state) => threadPendingInteractions(state, threadId));
  const openApprovalIds = new Set(pendingInteractions.map((interaction) => interaction.interactionId || interaction.id));
  const endRef = useRef(null);
  const lastBlock = blocks[blocks.length - 1];
  const lastSignature = `${blocks.length}:${lastBlock?.kind === 'reply' ? lastBlock.text.length : lastBlock?.steps?.length || 0}`;

  useEffect(() => {
    endRef.current?.scrollIntoView?.({ block: 'end' });
  }, [lastSignature]);

  if (blocks.length === 0) {
    return <p className="assistant-timeline__empty">Nothing here yet. Write what you need below and Pieve starts on it.</p>;
  }

  return (
    <div className="assistant-timeline">
      {blocks.map((block) => {
        if (block.kind === 'request') {
          return <RequestBlock key={block.id} text={block.text} attachments={block.attachments} pending={block.pending} />;
        }
        if (block.kind === 'reply') {
          return <MarkdownText key={block.id} text={block.text} />;
        }
        if (block.kind === 'steps') {
          return <StepTrail key={block.id} steps={block.steps} />;
        }
        if (block.kind === 'approval') {
          return <ApprovalCard key={block.id} approval={block.approval} threadId={threadId} isOpen={openApprovalIds.has(block.approval.interactionId)} />;
        }
        if (block.kind === 'connection') {
          return <ConnectionCard key={block.id} connection={block.connection} threadId={threadId} />;
        }
        return <NoticeLine key={block.id} tone={block.tone} text={block.text} />;
      })}
      <div ref={endRef} />
    </div>
  );
};

export default ThreadTimeline;
